import { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { PlusIcon } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, ApiKeyCreateRequest, TenantResponse } from '../lib/api';
import ApiKeyDisplay from './ApiKeyDisplay'; // Import the new component

interface CreateApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenant: TenantResponse | null;
}

export default function CreateApiKeyModal({ isOpen, onClose, tenant }: CreateApiKeyModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [allowedOrigins, setAllowedOrigins] = useState('https://app-lawma-prod-ai-api-westus2-001-g9cshwd4bvhyh3bj.westus2-01.azurewebsites.net');
  const [generatedApiKey, setGeneratedApiKey] = useState('');
  const [error, setError] = useState('');
  const queryClient = useQueryClient();

  const createApiKeyMutation = useMutation({
    mutationFn: (data: ApiKeyCreateRequest) => apiClient.createApiKey(data),
    onSuccess: (response) => {
      setGeneratedApiKey(response.api_key);
      queryClient.invalidateQueries({ queryKey: ['apiKeys', tenant?.id] });
      queryClient.invalidateQueries({ queryKey: ['tenants'] }); // Invalidate tenants to update API key count
      setError('');
      setName('');
      setDescription('');
    },
    onError: (err: Error) => {
      setError(err.message || 'Failed to create API key.');
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenant) {
      setError('No tenant selected.');
      return;
    }

    const originsArray = allowedOrigins.split(',').map(s => s.trim()).filter(s => s.length > 0);

    const requestData: ApiKeyCreateRequest = {
      tenant_id: tenant.id,
      name: name || `API Key for ${tenant.name}`,
      description: description,
      allowed_origins: originsArray,
    };

    createApiKeyMutation.mutate(requestData);
  };

  const handleCloseModal = () => {
    setGeneratedApiKey(''); // Clear generated key when modal closes
    setError('');
    onClose();
  };

  if (generatedApiKey) {
    return <ApiKeyDisplay apiKey={generatedApiKey} onClose={handleCloseModal} />;
  }

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={handleCloseModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-6 py-8 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-8">
                <form onSubmit={handleSubmit}>
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 sm:mx-0 sm:h-10 sm:w-10">
                      <PlusIcon className="h-6 w-6 text-primary-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                        Generate API Key for {tenant?.name}
                      </Dialog.Title>
                      <div className="mt-6 space-y-6">
                        {error && (
                          <p className="text-sm text-red-600 mb-4">{error}</p>
                        )}
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                            Key Name (optional)
                          </label>
                          <input
                            type="text"
                            name="name"
                            id="name"
                            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:border-gray-400 sm:text-sm px-3 py-2"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g., Production Widget Key"
                          />
                        </div>
                        <div>
                          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                            Description (optional)
                          </label>
                          <textarea
                            id="description"
                            name="description"
                            rows={3}
                            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:border-gray-400 sm:text-sm px-3 py-2"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="e.g., Key for our main website chatbot integration"
                          />
                        </div>
                        <div>
                          <label htmlFor="allowedOrigins" className="block text-sm font-medium text-gray-700">
                            Allowed Origins (comma-separated, optional)
                          </label>
                          <input
                            type="text"
                            name="allowedOrigins"
                            id="allowedOrigins"
                            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:border-gray-400 sm:text-sm px-3 py-2"
                            value={allowedOrigins}
                            onChange={(e) => setAllowedOrigins(e.target.value)}
                            placeholder="e.g., https://example.com, https://staging.example.com"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            List domains that can use this key (e.g., your website URL). Leave empty for no restrictions.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-8 sm:flex sm:flex-row-reverse">
                    <button
                      type="submit"
                      className="inline-flex w-full justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                      disabled={createApiKeyMutation.isLoading}
                    >
                      {createApiKeyMutation.isLoading ? 'Generating...' : 'Generate Key'}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none sm:mt-0 sm:w-auto sm:text-sm"
                      onClick={handleCloseModal}
                      disabled={createApiKeyMutation.isLoading}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
